// ============================================================
// VISION DESIGN SYSTEM — Fase 9.19
// Componentes base para Computer Vision UI
// ============================================================
import { motion, AnimatePresence } from 'framer-motion'
import { Camera, Wifi, WifiOff, AlertTriangle, CheckCircle2, Eye, Zap } from 'lucide-react'
import { TEXT, RISK, SEVERITY } from '@/design-system/tokens'
import type { CameraDevice, VisionEvent, PPEViolation } from '@/types/vision'
import { getStatusColor, getStatusLabel } from '@/core/cameras/cameraManager'

// ─── Stream Status badge ──────────────────────────────────────
export function StreamStatus({ status }: { status: CameraDevice['status'] }) {
  const color = getStatusColor(status)
  const label = getStatusLabel(status)
  const isOnline = status === 'online'

  return (
    <div className="flex items-center gap-1.5">
      <motion.div
        className="w-1.5 h-1.5 rounded-full"
        style={{ background: color }}
        animate={isOnline ? { opacity: [1, 0.4, 1] } : { opacity: 1 }}
        transition={{ duration: 2, repeat: Infinity }}
      />
      <span className="text-[9px] font-bold" style={{ color }}>
        {label}
      </span>
    </div>
  )
}

// ─── Camera Card ──────────────────────────────────────────────
interface CameraCardProps {
  camera:     CameraDevice
  isSelected: boolean
  onClick:    () => void
  compact?:   boolean
  hasAlert?:  boolean
  alertCount?: number
}

export function CameraCard({
  camera, isSelected, onClick, compact = false, hasAlert = false, alertCount = 0,
}: CameraCardProps) {
  const borderColor = isSelected
    ? 'rgba(249,115,22,0.5)'
    : hasAlert
      ? 'rgba(239,68,68,0.3)'
      : 'rgba(255,255,255,0.08)'

  return (
    <motion.button
      layout
      onClick={onClick}
      whileHover={{ scale: 1.01 }}
      whileTap={{ scale: 0.99 }}
      className={`relative text-left rounded-xl overflow-hidden transition-all ${compact ? 'p-2' : 'p-3'}`}
      style={{
        background: isSelected ? 'rgba(249,115,22,0.08)' : 'rgba(255,255,255,0.03)',
        border:     `1.5px solid ${borderColor}`,
      }}
    >
      {/* Preview area */}
      <div
        className="rounded-lg mb-2 flex items-center justify-center relative"
        style={{
          background: 'rgba(0,0,0,0.4)',
          aspectRatio: '16/9',
          border: '1px solid rgba(255,255,255,0.05)',
        }}
      >
        {camera.snapshot_url ? (
          <img
            src={camera.snapshot_url}
            alt={camera.nome}
            className="w-full h-full object-cover rounded-lg"
            onError={e => { (e.target as HTMLImageElement).style.display = 'none' }}
          />
        ) : (
          <Camera size={24} style={{ color: TEXT.faint, opacity: 0.4 }} />
        )}

        {/* Status overlay */}
        <div className="absolute top-1.5 left-1.5">
          <StreamStatus status={camera.status} />
        </div>

        {/* Alert badge */}
        {alertCount > 0 && (
          <motion.div
            animate={{ scale: [1, 1.1, 1] }}
            transition={{ duration: 1.5, repeat: Infinity }}
            className="absolute top-1.5 right-1.5 text-[9px] font-black px-1.5 py-0.5 rounded-full"
            style={{ background: 'rgba(239,68,68,0.9)', color: 'white' }}
          >
            {alertCount}
          </motion.div>
        )}

        {/* Recording indicator */}
        {camera.status === 'online' && (
          <div className="absolute bottom-1.5 right-1.5 flex items-center gap-1">
            <motion.div
              className="w-1.5 h-1.5 rounded-full bg-red-500"
              animate={{ opacity: [1, 0.3, 1] }}
              transition={{ duration: 1.2, repeat: Infinity }}
            />
            <span className="text-[8px] text-red-400 font-bold">REC</span>
          </div>
        )}
      </div>

      {!compact && (
        <>
          <div className="text-[11px] font-bold text-white truncate">{camera.nome}</div>
          {camera.location_desc && (
            <div className="text-[9px] mt-0.5 truncate" style={{ color: TEXT.faint }}>
              📍 {camera.location_desc}
            </div>
          )}
          <div className="flex items-center justify-between mt-1.5">
            <span className="text-[9px]" style={{ color: TEXT.faint }}>{camera.tipo}</span>
            <span className="text-[9px]" style={{ color: TEXT.faint }}>{camera.resolution}</span>
          </div>
        </>
      )}
    </motion.button>
  )
}

// ─── Detection Badge ──────────────────────────────────────────
interface DetectionBadgeProps {
  label:    string
  count?:   number
  severity: 'ok' | 'info' | 'warning' | 'critical' | 'emergency'
  icon?:    React.ReactNode
}

export function DetectionBadge({ label, count, severity, icon }: DetectionBadgeProps) {
  const colors: Record<string, { bg: string; text: string; border: string }> = {
    ok:        { bg: 'rgba(52,211,153,0.1)',  text: '#34d399', border: 'rgba(52,211,153,0.25)'  },
    info:      { bg: 'rgba(96,165,250,0.1)',  text: '#60a5fa', border: 'rgba(96,165,250,0.25)'  },
    warning:   { bg: 'rgba(251,191,36,0.1)',  text: '#fbbf24', border: 'rgba(251,191,36,0.25)'  },
    critical:  { bg: 'rgba(248,113,113,0.12)',text: '#f87171', border: 'rgba(248,113,113,0.3)'  },
    emergency: { bg: 'rgba(239,68,68,0.15)',  text: '#ef4444', border: 'rgba(239,68,68,0.35)'   },
  }

  const c = colors[severity] ?? colors.info

  return (
    <div className="flex items-center gap-1.5 px-2 py-1 rounded-full"
      style={{ background: c.bg, border: `1px solid ${c.border}` }}>
      {icon && <span style={{ color: c.text }}>{icon}</span>}
      <span className="text-[10px] font-bold" style={{ color: c.text }}>{label}</span>
      {count !== undefined && (
        <span className="text-[10px] font-black" style={{ color: c.text }}>{count}</span>
      )}
    </div>
  )
}

// ─── Vision Event Row ─────────────────────────────────────────
export function VisionEventRow({
  event, onResolve,
}: { event: VisionEvent; onResolve?: (id: string) => void }) {
  const sev = SEVERITY[event.severity as keyof typeof SEVERITY] ?? SEVERITY.info

  return (
    <motion.div
      layout
      initial={{ opacity: 0, x: -12 }}
      animate={{ opacity: event.is_resolved ? 0.4 : 1, x: 0 }}
      exit={{ opacity: 0, height: 0 }}
      className="flex items-start gap-3 py-2"
      style={{ borderBottom: '1px solid rgba(255,255,255,0.04)' }}
    >
      <div className="w-5 h-5 rounded-full flex items-center justify-center shrink-0 mt-0.5"
        style={{ background: sev.bg, border: `1px solid ${sev.dot}` }}>
        <Eye size={9} style={{ color: sev.dot }} />
      </div>

      <div className="flex-1 min-w-0">
        <div className="text-xs font-bold text-white leading-snug">{event.title}</div>
        {event.detail && (
          <div className="text-[10px] mt-0.5" style={{ color: TEXT.muted }}>{event.detail}</div>
        )}
        <div className="text-[9px] mt-0.5 font-mono" style={{ color: TEXT.faint }}>
          {new Date(event.ts).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}
          {' · '}
          {(event.confidence * 100).toFixed(0)}% conf.
        </div>
      </div>

      {onResolve && !event.is_resolved && (
        <button
          onClick={() => onResolve(event.id)}
          className="shrink-0 text-[9px] px-2 py-0.5 rounded-full"
          style={{ background: 'rgba(255,255,255,0.06)', color: TEXT.muted }}
        >
          ✓
        </button>
      )}
    </motion.div>
  )
}

// ─── PPE Violation Card ───────────────────────────────────────
export function PPEViolationCard({
  violation, onAcknowledge,
}: { violation: PPEViolation; onAcknowledge?: (id: string) => void }) {
  const color = violation.is_critical ? '#ef4444' : '#fbbf24'

  return (
    <motion.div
      initial={{ opacity: 0, y: 4 }}
      animate={{ opacity: violation.acknowledged ? 0.45 : 1, y: 0 }}
      className="rounded-xl px-3 py-2.5"
      style={{ background: `${color}08`, border: `1px solid ${color}25` }}
    >
      <div className="flex items-start justify-between gap-2">
        <div className="flex-1">
          <div className="text-[11px] font-black" style={{ color }}>
            {violation.is_critical ? '🚨 Violação Crítica' : '⚠ Violação de EPI'}
          </div>
          <div className="text-[10px] mt-0.5" style={{ color: TEXT.muted }}>
            EPI ausente: {violation.missing_ppe.join(', ')}
          </div>
          {violation.zone_tipo && (
            <div className="text-[9px] mt-0.5" style={{ color: TEXT.faint }}>
              Zona: {violation.zone_tipo}
            </div>
          )}
        </div>
        {onAcknowledge && !violation.acknowledged && (
          <button
            onClick={() => onAcknowledge(violation.id)}
            className="text-[9px] px-2 py-1 rounded-lg shrink-0"
            style={{ background: `${color}18`, color }}
          >
            Ciente
          </button>
        )}
      </div>
    </motion.div>
  )
}

// ─── Vision Risk Zone Overlay ─────────────────────────────────
interface RiskZoneOverlayProps {
  zone: { nome: string; risk_level: string; polygon?: Array<{ x: number; y: number }> }
  containerW: number
  containerH: number
}

export function RiskZoneOverlay({ zone, containerW, containerH }: RiskZoneOverlayProps) {
  if (!zone.polygon?.length) return null

  const color = zone.risk_level === 'critical' ? '#ef4444'
    : zone.risk_level === 'high'     ? '#f87171'
    : zone.risk_level === 'moderate' ? '#fbbf24'
    : 'rgba(96,165,250,0.6)'

  const points = zone.polygon
    .map(p => `${p.x * containerW},${p.y * containerH}`)
    .join(' ')

  return (
    <svg
      className="absolute inset-0 pointer-events-none"
      width={containerW}
      height={containerH}
    >
      <polygon
        points={points}
        fill={`${color}15`}
        stroke={color}
        strokeWidth={1.5}
        strokeDasharray="4 3"
      />
    </svg>
  )
}

// ─── Vision Timeline Card ─────────────────────────────────────
export function VisionTimelineCard({ snapshot }: { snapshot: { taken_at: string; trigger: string; analysis_done: boolean } }) {
  return (
    <div className="flex items-center gap-2 py-1.5"
      style={{ borderBottom: '1px solid rgba(255,255,255,0.04)' }}>
      <div className="w-1.5 h-1.5 rounded-full shrink-0" style={{ background: snapshot.analysis_done ? '#34d399' : TEXT.faint as string }} />
      <span className="text-[10px] font-mono" style={{ color: TEXT.muted }}>
        {new Date(snapshot.taken_at).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}
      </span>
      <span className="text-[9px] px-1.5 py-0.5 rounded"
        style={{ background: 'rgba(255,255,255,0.05)', color: TEXT.faint }}>
        {snapshot.trigger}
      </span>
      {snapshot.analysis_done && (
        <CheckCircle2 size={9} style={{ color: '#34d399', marginLeft: 'auto' }} />
      )}
    </div>
  )
}
